import { FC, memo, MutableRefObject, useEffect, useState } from 'react';
import styled from 'styled-components';

import { AssetPhoto } from '@/_pages/assets/asset/info/_pageModule/components/Gallery/Photos';
import { COLORS } from '@/shared/constants/colors';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { Icon } from '@/shared/uikit/Icon';
import { Spinner } from '@/shared/uikitV2/Spinner';
import { files } from '@/shared/utils/files';

export type CarouselOptions = {
  getFile: (id: DtoId) => Promise<File | undefined>;
  images: Array<AssetPhoto>;
  initialIndex: number;
  decryptedPhotoIdsRef: MutableRefObject<Set<string>>;
};

export type CarouselProps = CarouselOptions & {
  onChangeIndex: (newIndex: number) => void;
};

export const Carousel: FC<CarouselProps> = memo(
  ({ getFile, images, initialIndex, decryptedPhotoIdsRef, onChangeIndex }) => {
    const [index, setIndex] = useState(initialIndex);
    const [items, setItems] = useState(images);

    const handlePrev = () => {
      setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
    };

    const handleNext = () => {
      setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
    };

    useEffect(() => {
      onChangeIndex(index);
      const decryptedPhotoId = items[index].id;

      if (!decryptedPhotoIdsRef.current.has(decryptedPhotoId)) {
        getFile(decryptedPhotoId).then((file) => {
          const newState = [...items];
          newState[index].file = file;
          setItems(newState);
          decryptedPhotoIdsRef.current.add(decryptedPhotoId);
        });
      }
    }, [index]);

    return (
      <Wrapper>
        <Viewport>
          <Track style={{ transform: `translateX(-${index * 100}%)` }}>
            {items.map(({ id, file }) =>
              file instanceof File ? (
                <Img key={id} src={files.blobToUrl(file)} alt="" />
              ) : (
                <Container key={id}>
                  <Spinner size="md" />
                </Container>
              ),
            )}
          </Track>
        </Viewport>
        <Button style={{ left: '20px' }} onClick={handlePrev}>
          <Icon name="arrow-left" size="s20" />
        </Button>
        <Button style={{ right: '20px' }} onClick={handleNext}>
          <Icon name="arrow-right" size="s20" />
        </Button>
      </Wrapper>
    );
  },
);

Carousel.displayName = 'Carousel';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
`;

const Viewport = styled.div`
  width: 1340px;
  height: 678px;
  align-self: flex-start;
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.4s ease;
`;

const Button = styled.button`
  position: absolute;
  padding: 12px;
  background: ${COLORS.systemDark50A};
  color: ${COLORS.systemLight100};
  border: none;
  border-radius: 8px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;
`;
